import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { LoginModel } from '../model/login-model';
import { SignUp } from '../model/sign-up.model';
import { Setting } from '../setting';
import { BaseService } from './base.services';
import { UserService } from './user.service';

export interface GoogleLoginConfig {
  enabled: boolean;
  clientId?: string | null;
}

export interface MicrosoftLoginConfig {
  enabled: boolean;
  clientId?: string | null;
  authority?: string | null;
  nonce?: string | null;
  challenge?: string | null;
  challengeExpiresAt?: number | null;
}

export type MicrosoftLoginResponse = {
  data: { token: string; current_logged_organization?: unknown };
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    public setting: Setting,
    public baseService: BaseService<any>,
    public userService: UserService,
  ) {}

  login(user: LoginModel): Promise<any> {
    let promesa = new Promise((resolve, reject) => {
      this.baseService.post(this.setting.Endpoint.Login, user).subscribe({
        next: response => {
          resolve(response);
        },
        error: error => {
          reject(error);
        },
      });
    });

    return promesa;
  }

  googleConfig(): Promise<{ data: GoogleLoginConfig }> {
    return firstValueFrom(
      this.baseService.get<{ data: GoogleLoginConfig }>(
        `${this.setting.Endpoint.Login}/google/config`,
      ),
    );
  }

  loginWithGoogle(credential: string): Promise<any> {
    return firstValueFrom(
      this.baseService.post(`${this.setting.Endpoint.Login}/google`, { credential }),
    );
  }

  microsoftConfig(): Promise<{ data: MicrosoftLoginConfig }> {
    return firstValueFrom(
      this.baseService
        .get<{ data: MicrosoftLoginConfig }>(`${this.setting.Endpoint.Login}/microsoft/config`)
        .pipe(timeout(15000)),
    );
  }

  loginWithMicrosoft(credential: string, challenge: string): Promise<MicrosoftLoginResponse> {
    return firstValueFrom(
      this.baseService
        .post(`${this.setting.Endpoint.Login}/microsoft`, { credential, challenge })
        .pipe(timeout(30000)),
    );
  }

  signUp(payload: { user: SignUp; organization: any }): Promise<any> {
    let promesa = new Promise((resolve, reject) => {
      this.baseService.post(`${this.setting.Endpoint.SignUp}`, payload).subscribe({
        next: response => {
          resolve(response);
        },
        error: error => {
          reject(error);
        },
      });
    });

    return promesa;
  }

  sendEmail(email: string): Promise<any> {
    let promesa = new Promise((resolve, reject) => {
      this.baseService.post('/v1/users/forgot-password', { email: email }).subscribe(
        response => {
          resolve(response);
        },
        error => {
          reject(error.error.message);
        },
      );
    });

    return promesa;
  }

  verifyCode(email: string, code: string): Promise<any> {
    let promesa = new Promise((resolve, reject) => {
      this.baseService.post('/v1/users/validate-code', { email: email, code: code }).subscribe(
        response => {
          resolve(response);
        },
        error => {
          reject(error.error.message);
        },
      );
    });

    return promesa;
  }

  changePassword(email: string, code: string, newPassword: string): Promise<any> {
    const body = {
      email: email,
      code: code,
      password: newPassword,
    };
    console.log(body);
    let promesa = new Promise((resolve, reject) => {
      this.baseService.post('/v1/users/reset-password', body).subscribe(
        response => {
          resolve(response);
        },
        error => {
          reject(error.error.message);
        },
      );
    });

    return promesa;
  }

  switchOrganization(organizationId: number): Promise<any> {
    let promesa = new Promise((resolve, reject) => {
      this.baseService
        .post(`${this.setting.Endpoint.Login}/switch-organization`, { orgId: organizationId })
        .subscribe({
          next: response => {
            resolve(response);
          },
          error: error => {
            reject(error);
          },
        });
    });

    return promesa;
  }

  /**
   * Reemite el JWT de la sesión actual (misma organización) con los `orgModules` y `access` vigentes en BD.
   * `POST /auth/refresh-session` no lleva body y no registra evento; la pantalla «Módulos» lo llama tras
   * cambiar un módulo de la organización actual y pasa el token a `UserSettingsService.setUser`.
   */
  refreshSession(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.baseService.post(`${this.setting.Endpoint.Login}/refresh-session`, {}).subscribe({
        next: response => {
          resolve(response);
        },
        error: error => {
          reject(error);
        },
      });
    });
  }
}
